/**
 * Slider ranges, presets, and localStorage persistence for the live
 * feature-extraction settings (see ``ExtractionSettings`` in ``features.ts``).
 * Mirrors the ``uiTheme.ts`` storage pattern: validating reads, silent writes.
 */

import { readStored, writeStored } from "../domain/storage";
import { DEFAULT_EXTRACTION_SETTINGS, type ExtractionSettings } from "./features";

export const EXTRACTION_STORAGE_KEY = "sdss-quasar-extraction";

export interface ExtractionRange {
  min: number;
  max: number;
  step: number;
}

/** Slider bounds per setting; stored values are clamped to these on read. */
export const EXTRACTION_RANGES: Record<keyof ExtractionSettings, ExtractionRange> = {
  minSnr: { min: 2, max: 12, step: 0.5 },
  minBaseWidthA: { min: 4, max: 60, step: 1 },
  maxBaseWidthA: { min: 100, max: 1000, step: 10 },
  edgeMarginA: { min: 0, max: 200, step: 5 },
  knownMatchToleranceA: { min: 5, max: 50, step: 1 },
  maxDiscoveryFeatures: { min: 0, max: 20, step: 1 },
};

export interface ExtractionPreset {
  id: "strict" | "default" | "sensitive";
  label: string;
  settings: ExtractionSettings;
}

export const EXTRACTION_PRESETS: ExtractionPreset[] = [
  {
    id: "strict",
    label: "Strict",
    settings: {
      minSnr: 8,
      minBaseWidthA: 20,
      maxBaseWidthA: 400,
      edgeMarginA: 120,
      knownMatchToleranceA: 12,
      maxDiscoveryFeatures: 5,
    },
  },
  { id: "default", label: "Default", settings: { ...DEFAULT_EXTRACTION_SETTINGS } },
  {
    id: "sensitive",
    label: "Sensitive",
    settings: {
      minSnr: 3,
      minBaseWidthA: 6,
      maxBaseWidthA: 800,
      edgeMarginA: 30,
      knownMatchToleranceA: 25,
      maxDiscoveryFeatures: 16,
    },
  },
];

export function extractionSettingsEqual(
  a: ExtractionSettings,
  b: ExtractionSettings,
): boolean {
  return (Object.keys(EXTRACTION_RANGES) as Array<keyof ExtractionSettings>).every(
    (key) => a[key] === b[key],
  );
}

/** Coerce one stored field: finite number clamped to its slider range. */
function parseField(key: keyof ExtractionSettings, raw: unknown): number {
  const range = EXTRACTION_RANGES[key];
  const value = typeof raw === "number" && Number.isFinite(raw) ? raw : NaN;
  if (Number.isNaN(value)) return DEFAULT_EXTRACTION_SETTINGS[key];
  return Math.min(range.max, Math.max(range.min, value));
}

export function readStoredExtractionSettings(): ExtractionSettings {
  const raw = readStored(EXTRACTION_STORAGE_KEY);
  if (!raw) return { ...DEFAULT_EXTRACTION_SETTINGS };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return { ...DEFAULT_EXTRACTION_SETTINGS };
    }
    const record = parsed as Record<string, unknown>;
    const out = { ...DEFAULT_EXTRACTION_SETTINGS };
    for (const key of Object.keys(EXTRACTION_RANGES) as Array<keyof ExtractionSettings>) {
      out[key] = parseField(key, record[key]);
    }
    return out;
  } catch {
    return { ...DEFAULT_EXTRACTION_SETTINGS };
  }
}

export function writeStoredExtractionSettings(settings: ExtractionSettings): void {
  writeStored(EXTRACTION_STORAGE_KEY, JSON.stringify(settings));
}
